import Styles from '../ShapesTab.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { useUpdateStyle } from 'components/VisualStudioPlayground/VisualEditor/hooks/useUpdateStyle'
import { Editor, SharedStyle, StyleProp, track } from '@tldraw/tldraw'
import { FillStyle, IFillStyle, TextColorStyle } from 'components/VisualStudioPlayground/VisualEditor/StyleProps'
import { useRelevantStyles } from 'components/VisualStudioPlayground/VisualEditor/hooks/useRelevantStyles'
import { ImageShape } from 'components/VisualStudioPlayground/Shapes/ImageShape/Types'
import { ColorSetSection } from './ColorSetSection'
import { Color } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { getColors } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ChevronRightSmall } from 'blaze-ui/icons/24'
import { ColorSwatch } from 'components/Design/ColorSwatch/ColorSwatch'
import { Palette } from 'blaze-ui/icons'

interface Props {
  editor: Editor
}

interface ColorSwatchListProps {
  editor: Editor
  style: SharedStyle<string | IFillStyle>
  styleType: StyleProp<string | IFillStyle>
}

const ColorSwatchList = observer(({ editor, style, styleType }: ColorSwatchListProps) => {
  const { designStore } = useRootStore()
  const updateStyle = useUpdateStyle(editor)

  if (style.type === 'mixed') return null

  const currentColor =
    typeof style.value === 'string' ? ({ type: 'solidColor', color: style.value, opacity: 1 } as Color) : style.value

  const handleSeeAll = () => {
    if (!designStore) return

    designStore.openPanel({
      type: 'colors',
      styleProp: styleType,
      defaultColor: currentColor,
      colors: Array.from(getColors(editor).values()),
      onChange: (color: Color) => {
        if (styleType === TextColorStyle) {
          updateStyle(styleType, color.type === 'solidColor' ? color.color : undefined)
        } else if (styleType === FillStyle) {
          updateStyle(styleType, color)
        }
      },
      allowTransparency: styleType === TextColorStyle ? false : true,
      allowGradient: styleType === TextColorStyle ? false : true,
    })
  }

  return (
    <div className={Styles.actionRow} onClick={() => handleSeeAll()}>
      <div>
        <ColorSwatch color={currentColor} size={20} />
      </div>
      <span className={Styles.actionName}>
        <Text variant="smallList">Color</Text>
      </span>
      <ChevronRightSmall size={20} color="var(--dark-60)" />
    </div>
  )
})

export const ColorRow = track(({ editor }: Props) => {
  const relevantStyles = useRelevantStyles(editor)
  const selectedShape = editor.getOnlySelectedShape()

  if (!relevantStyles || !selectedShape) return null

  const { styles } = relevantStyles

  const textColor = styles.get(TextColorStyle)
  const fillStyle = styles.get(FillStyle)
  const colorSet = selectedShape?.meta.colorSet

  if (!fillStyle && !colorSet && !textColor) return null

  return (
    <div className={Styles.colorRow}>
      {textColor && <ColorSwatchList editor={editor} style={textColor} styleType={TextColorStyle} />}
      {colorSet && (
        <div className={Styles.colorSetRow}>
          <div className={Styles.colorSetRowHeader}>
            <Palette color="var(--selected-fill)" />
            <Text variant="smallList">Graphic Colors</Text>
          </div>
          <div className={Styles.colorsGrid}>
            <ColorSetSection editor={editor} shape={selectedShape as ImageShape} />
          </div>
        </div>
      )}
      {fillStyle && <ColorSwatchList editor={editor} style={fillStyle} styleType={FillStyle} />}
    </div>
  )
})
